/// <reference path="koyori.ts" />

/**
 * GoogleMapを使用できるようにするカスタムタグです。
 * 
 * <dl>
 * <dt>属性</dt>
 * <dd>key: GoogleApiKeyを指定します（注意：APIキーは不特定多数の人が見れない状態にする必要があります）</dd>
 * <dd>lat: 地図の中心の緯度</dd>
 * <dd>lng: 地図の中心の軽度</dd>
 * <dd>address: 初期位置の住所</dd>
 * <dd>zoom: 初期ズーム値</dd>
 * <dd>map-type-control: 地図種別ボタンの表示</dd>
 * <dd>zoom-control: 拡大・縮小ボタンの表示</dd>
 * <dd>fullscreen-control: 全画面モードボタンの表示</dd>
 * <dd>rotate-control: 衛星写真の回転ボタンの表示</dd>
 * <dd>street-view-control: ストリートビューボタンの表示</dd>
 * </dl>
 * <dl>
 * <dt>イベント</dt>
 * <dd>ready: 地図の準備が完了した場合</dd>
 * <dd>geocode-error: 住所変換でエラーが発生した場合</dd>
 * <dd>marker-click: マーカーがクリックされた場合</dd>
 * <dd>marker-dblclick: マーカーがダブルクリックされた場合</dd>
 * <dd>center-changed: 中心が移動した場合</dd>
 * </dl>
 */
class KoyoriGoogleMap extends KoyoriElement {

	/** リサイズ用タイマ */
	private resizeTimer: NodeJS.Timer;

	/** 中央移動用タイマ */
	private centerChangedTimer: NodeJS.Timer;

	/** 倍率変更用タイマ */
	private zoomChangedTimer: NodeJS.Timer;

	/** マップ */
	private map: google.maps.Map;

	/** マーカーの連想配列 */
	private markers: { [key: string]: google.maps.Marker } = {};

	constructor() {
		super();
	}

	connectedCallback(): void {
		window.addEventListener("resize", () => {
			if (this.resizeTimer != undefined) {
				clearTimeout(this.resizeTimer);
			}
			this.resizeTimer = setTimeout(() => {
				this.reLayout()
				this.resizeTimer = undefined;
			}, 100);
		});
		setTimeout(() => {
			this.reLayout();
		}, 100);
		this.reLayout();

		if (this.map == undefined) {
			let script = document.createElement('script');
			script.setAttribute('async', '');
			script.setAttribute('defer', '');
			script.setAttribute('src', 'https://maps.googleapis.com/maps/api/js?key=' + this.getAttribute('key'));
			script.addEventListener('load', () => {
				let address = this.getAttribute('address');
				if (address != null) {
					new google.maps.Geocoder().geocode({
						address: address
					}, (results, status) => {
						if (status == google.maps.GeocoderStatus.OK) {
							this.map = new google.maps.Map(this, this.getMapOption(results[0].geometry.location));
							google.maps.event.addListenerOnce(this.map, 'idle', () => {
								KoyoriElement.fireEvent(this, 'ready');
							});
						} else {
							KoyoriElement.fireEvent(this, 'geocode-error', true, true, status);
						}
					});
				} else {
					this.map = new google.maps.Map(this, this.getMapOption());
					google.maps.event.addListenerOnce(this.map, 'idle', () => {
						KoyoriElement.fireEvent(this, 'ready');
					});
				}
				this.map.addListener('center_changed', () => {
					let center = this.map.getCenter();
					this.setAttribute('lat', center.lat().toString());
					this.setAttribute('lng', center.lng().toString());
					if (this.centerChangedTimer != undefined) {
						clearTimeout(this.centerChangedTimer);
					}
					this.centerChangedTimer = setTimeout(() => {
						KoyoriElement.fireEvent(this, 'center-changed', true, true, center);
						this.centerChangedTimer = undefined;
					}, 500);
				});
				this.map.addListener('zoom_changed', () => {
					let zoom = this.map.getZoom();
					this.setAttribute('zoom', zoom.toString());
					if (this.zoomChangedTimer != undefined) {
						clearTimeout(this.zoomChangedTimer);
					}
					this.zoomChangedTimer = setTimeout(() => {
						KoyoriElement.fireEvent(this, 'zoom-changed', true, true, zoom);
						this.zoomChangedTimer = undefined;
					}, 500);
				});
			});
			document.body.appendChild(script);
		}
	}

	/**
	 * レイアウトを調整します。
	 */
	reLayout(): void {
		let parent = this.parentElement;
		parent.style.overflowY = 'hidden';

		let height = KoyoriElement.getFitHeight(this);
		this.style.height = height + "px";
	}

	/**
	 * @return 地図オブジェクト
	 */
	getMap(): google.maps.Map {
		return this.map;
	}

	/**
	 * マーカーを追加もしくは更新します。
	 * 
	 * @param key
	 * @param options
	 */
	setMarker(key: string, options: google.maps.MarkerOptions): google.maps.Marker {
		let marker = this.markers[key];
		if (marker) {
			marker.setOptions(options);
		} else {
			options.map = this.map;
			marker = new google.maps.Marker(options);
			let detail = {
				'key': key,
				'marker': marker,
				'options': options
			}
			marker.addListener('click', (event) => {
				KoyoriElement.fireEvent(this, 'marker-click', true, true, detail);
			});
			marker.addListener('dblclick', (event) => {
				KoyoriElement.fireEvent(this, 'marker-dblclick', true, true, detail);
			});
			this.markers[key] = marker;
		}
		return marker;
	}

	/**
	 * マーカーを削除します。
	 * 
	 * @param key
	 */
	removeMarker(key: string): void {
		let marker = this.markers[key];
		if (marker) {
			marker.setMap(null);
			this.markers[key] = undefined;
		}
	}

	getMarker(key: string): google.maps.Marker {
		return this.markers[key];
	}

	getMarkers(): google.maps.Marker[] {
		let list: google.maps.Marker[] = [];
		Object.keys(this.markers).forEach(key => {
			list.push(this.markers[key]);
		});
		return list;
	}

	/**
	 * 指定された住所に移動します。
	 * 
	 * @param target
	 */
	panTo(target: string | google.maps.LatLng) {
		if (typeof target == 'string') {
			new google.maps.Geocoder().geocode({
				address: target
			}, (results, status) => {
				if (status == google.maps.GeocoderStatus.OK) {
					this.map.panTo(results[0].geometry.location);
				} else {
					KoyoriElement.fireEvent(this, 'geocode-error', true, true, status);
				}
			});
		} else {
			this.map.panTo(target);
		}
	}

	private getMapOption(latLng: google.maps.LatLng = null): google.maps.MapOptions {
		return {
			center: latLng != null ? latLng : {
				lat: parseFloat(this.getAttribute('lat') || '35.65'),
				lng: parseFloat(this.getAttribute('lng') || '139.7333')
			},
			zoom: parseInt(this.getAttribute('zoom') || '16'),
			mapTypeControl: (this.getAttribute('map-type-control') != null) ? true : false,
			zoomControl: (this.getAttribute('zoom-control') != null) ? true : false,
			fullscreenControl: (this.getAttribute('fullscreen-control') != null) ? true : false,
			rotateControl: (this.getAttribute('rotate-control') != null) ? true : false,
			streetViewControl: (this.getAttribute('street-view-control') != null) ? true : false
		}
	}

}
window.customElements.define('koy-google-map', KoyoriGoogleMap);
